import {
  Box,
  Button,
  Grid,
  Typography,
  makeStyles,
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormHelperText,
} from '@material-ui/core';
import { useFormik } from 'formik';
import React from 'react';
import * as yup from 'yup';

import { Routers } from '../../../../config/constants';
import { formatPhoneReadable, parsePhone } from '../../../../helpers/helpers';
import locales from '../../../../locales';
import { useDealerRegistrationMutation } from '../model';

const initialValues: GM.DealerRegistrationForm.Props = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  password: '',
  passwordConfirmation: '',
  acceptRules: false,
};

const requiredMessage = 'Обязательно для заполнения';
const validationSchema = yup.object({
  firstName: yup
    .string()
    .required(requiredMessage)
    .min(3, 'Должно содержать минимум 3 символа')
    .matches(/^[\p{L}\s-]+$/u, 'Может содержать только буквы, пробелы и дефис'),
  lastName: yup
    .string()
    .required(requiredMessage)
    .min(3, 'Должно содержать минимум 3 символа')
    .matches(/^[\p{L}\s-]+$/u, 'Может содержать только буквы, пробелы и дефис'),
  phone: yup
    .string()
    .required(requiredMessage)
    .test('phone-valid', 'Введите корректный номер телефона', (value) => {
      if (!value) return false;
      return parsePhone(value) !== false;
    }),
  email: yup.string().required(requiredMessage).email('Введите корректный адрес эл. почты'),
  acceptRules: yup
    .boolean()
    .required('Необходимо согласие с правилами')
    .oneOf([true], 'Необходимо согласие с правилами'),
  password: yup
    .string()
    .required(requiredMessage)
    .min(8, 'Пароль должен содержать минимум 8 символов'),
  passwordConfirmation: yup
    .string()
    .required(requiredMessage)
    .oneOf([yup.ref('password')], 'Пароли должны совпадать'),
});

const blackColor = '#1E1E28';

const themeVars = {
  '--gm-bg': '#fff',
  '--gm-text': 'rgba(0, 0, 0, 0.87)', // текст полей
  '--gm-text-muted': 'rgba(46, 46, 46, 1)', // лейблы, плейсхолдеры
  '--gm-field-bg': '#ffffff', // фон поля
  '--gm-border': 'rgba(196, 196, 196, 1)', // рамка поля
  '--gm-border-hover': 'rgba(196, 196, 196, 1)',
  '--gm-border-focus': 'rgba(196, 196, 196, 1)',
  '--gm-btn-bg': '#111014',
  '--gm-btn-bg-hover': blackColor,
  '--gm-btn-text': '#ffffff',
  '--gm-checkbox-checked': blackColor,
  '--gm-field-error-border': '#f44336',
  '--gm-error': '#f44336',
  '--gm-autofill-bg': '#ffffff',
};

const darkThemeVars = {
  '--gm-bg': '#09090b',
  '--gm-text': '#fafafa',
  '--gm-text-muted': '#b2b2ba',
  '--gm-field-bg': 'rgba(255, 255, 255, 0.04)',
  '--gm-border': 'rgba(255, 255, 255, 0.15)',
  '--gm-border-hover': 'rgba(255, 255, 255, 0.28)',
  '--gm-border-focus': '#a9a9b2',
  '--gm-btn-bg': '#e8e8ea',
  '--gm-btn-bg-hover': '#d8d8db',
  '--gm-btn-text': '#1c1c20',
  '--gm-checkbox-checked': '#e8e8ea',
  '--gm-field-error-border': '#ff6b6b',
  '--gm-error': '#ff6b6b',
  '--gm-autofill-bg': '#23232a',
};

const useStyles = makeStyles((theme) => ({
  // Фон body форма рисует сама, по теме. Задаём через @global напрямую значениями
  // (не через var(--gm-bg)): переменные объявлены на .root, а body выше по дереву —
  // до него они не дотягиваются, из-за чего снизу под формой оставалась белая полоса
  // от светлого background.default, который вешает CssBaseline.
  '@global': {
    '[data-theme="light"] body': { backgroundColor: themeVars['--gm-bg'] },
    '[data-theme="dark"] body': { backgroundColor: darkThemeVars['--gm-bg'] },
    html: {
      backgroundColor: 'transparent',
    },
  },
  // Правый бордер полей срезается окном iframe из-за negative margin у Grid spacing —
  // прячем горизонтальное переполнение на корневом контейнере формы.
  root: {
    ...themeVars,
    color: 'var(--gm-text)',
    '[data-theme="dark"] &': darkThemeVars,
  },
  wrapper: {
    backgroundColor: 'var(--gm-bg)',
    padding: 16,
    paddingBottom: 50,
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      '& fieldset': {
        borderColor: 'var(--gm-border)',
      },
      '&:hover:not(.Mui-error) fieldset': {
        borderColor: 'var(--gm-border-hover)',
      },
      '&.Mui-focused fieldset': {
        borderWidth: 1,
      },
      '&.Mui-focused:not(.Mui-error) fieldset': {
        borderColor: 'var(--gm-border-focus)',
      },
      '&.Mui-error fieldset': {
        borderColor: 'var(--gm-field-error-border)',
      },
    },
    '& .MuiOutlinedInput-input': {
      fontSize: 16,
      // paddingTop: 7,
      color: 'var(--gm-text)',
      '&::placeholder': {
        color: 'var(--gm-text-muted)',
        opacity: 1,
      },
      // Автозаполнение Chrome иначе вернёт белый фон и чёрный текст.
      '&:-webkit-autofill': {
        WebkitTextFillColor: 'var(--gm-text)',
        WebkitBoxShadow: '0 0 0 1000px var(--gm-autofill-bg) inset',
        caretColor: 'var(--gm-text)',
      },
    },
    '& .MuiInputLabel-outlined': {
      position: 'relative',
      transform: 'none',
      fontSize: 16,
      fontWeight: 400,
      marginBottom: theme.spacing(1.25),
      '&:not(.Mui-error)': {
        color: 'var(--gm-text-muted)',
      },
      '&.Mui-focused:not(.Mui-error)': {
        color: 'var(--gm-text)',
      },
      '&.MuiInputLabel-shrink': {
        transform: 'none',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      top: 0,
      '& legend': {
        display: 'none',
      },
    },
    '& .MuiFormHelperText-root.Mui-error': {
      color: 'var(--gm-error)',
    },
  },
  checkboxWrapper: {
    '& .MuiFormHelperText-root.Mui-error': {
      color: 'var(--gm-error)',
    },
    '& .MuiFormControlLabel-root': {
      alignItems: 'flex-start',
    },
    '& .MuiCheckbox-root': {
      paddingTop: 0,
      color: 'var(--gm-text-muted)',
      '&.Mui-checked': {
        color: 'var(--gm-checkbox-checked)',
      },
    },
    '& .MuiTypography-root': {
      fontSize: 16,
      paddingTop: 1,
      color: 'var(--gm-text)',
    },
  },
  rules: {
    display: 'block',
    marginTop: 4,
    '& a': {
      color: 'var(--gm-text)',
      textDecoration: 'none',
      borderBottomWidth: 1,
      borderBottomStyle: 'solid',
      paddingBottom: 2,
    },
  },
  submitButton: {
    backgroundColor: 'var(--gm-btn-bg)',
    color: 'var(--gm-btn-text)',
    padding: '12px',
    lineHeight: '19px',
    height: '44px',
    fontSize: 16,
    fontWeight: 500,
    borderRadius: 12,
    '&:hover': {
      backgroundColor: 'var(--gm-btn-bg-hover)',
      boxShadow: 'none',
    },
  },
  contentLink: {
    color: 'var(--gm-text)',
    textDecoration: 'none',
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    paddingBottom: 2,
  },
}));

export const DealerRegistrationFormWidget = () => {
  const classes = useStyles();

  const registration = useDealerRegistrationMutation();

  const formik = useFormik({
    validationSchema,
    initialValues,
    onSubmit: (props) => registration.mutate(props),
  });

  let successMessage = null;
  if (registration.isSuccess) {
    successMessage = (
      <>
        <Box fontSize={16} fontWeight={400} mb={2}>
          Регистрация прошла успешно! Мы отправили Вам письмо на&nbsp;указанный адрес электронной
          почты.
        </Box>

        <Box fontSize={16} fontWeight={600} mb={2}>
          Для подтверждения регистрации и&nbsp;активации учетной записи, перейдите по&nbsp;ссылке
          из&nbsp;письма.
        </Box>
      </>
    );
  }

  let errorMessage = null;
  if (registration.isError && registration.error) {
    errorMessage = 'Что-то пошло не&nbsp;так, попробуйте позже';

    // Ошибки от API не русифицированы, поэтому одно сообщение для всех случаев без деталей
    // if (Array.isArray(registration.error.response?.data)) {
    //   const backendErrors = registration.error.response.data.filter(err => err.message).map(err => err.message).join('. ')
    //   if (backendErrors) {
    //     errorMessage = backendErrors;
    //   }
    // }

    if (
      Array.isArray(registration.error.response?.data) &&
      registration.error.response.data.some((err) => err.code === 'email_already_used')
    ) {
      errorMessage = 'Пользователь с&nbsp;таким эл.&nbsp;адресом уже зарегистрирован';
    }
  }

  const helpMessage = (
    <Box fontSize={16} fontWeight={400}>
      Не получается зарегистрироваться? Напишите нам на&nbsp;
      <a className={classes.contentLink} href="mailto:info@guestme.ru">
        info@guestme.ru
      </a>
      , мы поможем решить проблему.
    </Box>
  );

  return (
    <Box className={`${classes.root} ${classes.wrapper}`}>
      {successMessage ? (
        <Grid container>
          <Grid item xs={12}>
            {successMessage}
            {helpMessage}
          </Grid>
        </Grid>
      ) : (
        <>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <TextField
                name="firstName"
                label="Имя"
                className={classes.textField}
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && !!formik.errors.firstName}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="lastName"
                label="Фамилия"
                className={classes.textField}
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={formik.touched.lastName && !!formik.errors.lastName}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="phone"
                label="Телефон"
                className={classes.textField}
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={formik.values.phone}
                onChange={formik.handleChange}
                error={formik.touched.phone && !!formik.errors.phone}
                helperText={formik.touched.phone && formik.errors.phone}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  const parsed = parsePhone(e.target.value);
                  if (parsed !== false) {
                    formik.setFieldValue('phone', formatPhoneReadable(parsed));
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="email"
                label="E-mail"
                className={classes.textField}
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="password"
                type="password"
                label="Пароль"
                className={classes.textField}
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="passwordConfirmation"
                type="password"
                label="Подтверждение пароля"
                className={classes.textField}
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={formik.values.passwordConfirmation}
                onChange={formik.handleChange}
                error={formik.touched.passwordConfirmation && !!formik.errors.passwordConfirmation}
                helperText={
                  formik.touched.passwordConfirmation && formik.errors.passwordConfirmation
                }
              />
            </Grid>

            <Grid item>
              <Box className={classes.checkboxWrapper}>
                <FormControl error={formik.touched.acceptRules && !!formik.errors.acceptRules}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="acceptRules"
                        checked={formik.values.acceptRules}
                        onChange={formik.handleChange}
                      />
                    }
                    label={
                      <>
                        {locales.t('common.agreeProcessingPersonalData')}{' '}
                        <span className={classes.rules}>
                          <a href={Routers.rules} target="_blank" rel="noreferrer">
                            {locales.t('common.useRules')}
                          </a>
                        </span>
                        {formik.touched.acceptRules && !!formik.errors.acceptRules && (
                          <Box mt={2}>
                            <FormHelperText>{formik.errors.acceptRules}</FormHelperText>
                          </Box>
                        )}
                      </>
                    }
                  />
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} container justify="center">
              <Button
                fullWidth
                variant="contained"
                className={classes.submitButton}
                onClick={() => formik.handleSubmit()}
                disabled={formik.submitCount > 0 && !formik.isValid}
              >
                Зарегистрироваться и&nbsp;начать работу
              </Button>
            </Grid>
          </Grid>

          {errorMessage && (
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Box mb={2}>
                  <Typography
                    variant="h6"
                    style={{ color: 'var(--gm-error)' }}
                    dangerouslySetInnerHTML={{
                      __html: errorMessage,
                    }}
                  />
                </Box>

                {helpMessage}
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};
